import { useEffect, useMemo, useState } from 'react'
import Toast from '../components/Toast.jsx'
import BackButton from '../components/BackButton.jsx'
import { listEmployees } from '../data/employees.js'
import { getObrasAtivas } from '../data/obras.js'
import { getConsumoPorFuncionario, getConsumoPorProduto, getTopPerdas, getEPIsVencidos, getEPIsPrestesVencer } from '../data/dashboard.js'
import { fetchMovimentacoes, fetchProdutos } from '../data/syncManager.js'
import { listNotasFiscais } from '../data/notasFiscais.js'
import { useAuth } from '../auth/AuthProvider.jsx'
import { drawPDFHeader, drawTableHeader, drawTableRow, drawTableCell, checkNewPage } from '../data/pdfUtils.js'
import { jsPDF } from 'jspdf'

function formatDate(ts) {
  if (!ts) return '—'
  try {
    return new Date(ts).toLocaleDateString('pt-BR')
  } catch {
    return '—'
  }
}

function getReportDescription(reportType) {
  const descriptions = {
    'consumo_funcionario': 'CONSUMO POR FUNCIONÁRIO',
    'consumo_produto': 'CONSUMO POR PRODUTO',
    'perdas': 'REGISTRO DE PERDAS',
    'epi_vencidos': 'EPIs VENCIDOS',
    'epi_prestes_vencer': 'EPIs PRESTES A VENCER'
  }
  return descriptions[reportType] || 'RELATÓRIO GERAL'
}

const REPORT_TYPES = [
  { id: 'consumo_funcionario', label: 'Consumo por funcionário' },
  { id: 'consumo_produto', label: 'Consumo por produto' },
  { id: 'perdas', label: 'Perdas recentes' },
  { id: 'pendentes', label: 'Pendentes de devolução' },
  { id: 'epi_motivos', label: 'EPIs por motivo' },
  { id: 'epi_vencidos', label: 'EPIs vencidos' },
  { id: 'epi_prestes_vencer', label: 'EPIs prestes a vencer' },
  { id: 'financeiro', label: 'Financeiro (custos)' },
  { id: 'consumo_obra', label: '🏗️ Consumo por obra' },
  { id: 'custo_obra', label: '🏗️ Custo total por obra' },
  { id: 'inventario_localizacao', label: '📍 Inventário por localização' },
  { id: 'produtos_nf', label: '📎 Produtos por nota fiscal' },
  { id: 'compras_fornecedor', label: '📎 Compras por fornecedor' }
]

const EPI_MOTIVOS = [
  { id: '', label: 'Todos' },
  { id: 'novo', label: 'Novo' },
  { id: 'perda', label: 'Perda' },
  { id: 'troca', label: 'Troca' }
]

export default function ReportsPage() {
  const { session } = useAuth()
  const [toast, setToast] = useState(null)
  const [toastType, setToastType] = useState('info')
  const [reportType, setReportType] = useState('consumo_funcionario')
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(false)
  const [employees, setEmployees] = useState([])
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('')
  const [detailModal, setDetailModal] = useState({ open: false, employee: null, movements: [] })
  const [selectedMotivo, setSelectedMotivo] = useState('')
  // Filtros adicionais apenas para o financeiro (não removem os existentes)
  const [financeiroCategoria, setFinanceiroCategoria] = useState('') // '', 'material', 'ferramenta', 'epi'
  const [financeiroTipo, setFinanceiroTipo] = useState('') // ex.: 'entrega', 'saida', 'perda', 'descarte', 'manutencao'
  // Filtros avançados
  const [dataInicial, setDataInicial] = useState('')
  const [dataFinal, setDataFinal] = useState('')
  const [tipoProduto, setTipoProduto] = useState('') // '', 'material', 'ferramenta', 'epi' (para todos os relatórios)
  const [valorMinimo, setValorMinimo] = useState('')
  const [valorMaximo, setValorMaximo] = useState('')
  const [statusFuncionario, setStatusFuncionario] = useState('') // '', 'ativo', 'inativo'
  const [filtroObra, setFiltroObra] = useState('')
  const [obras, setObras] = useState([])
  const [filtroFornecedor, setFiltroFornecedor] = useState('')
  const [filtroPossuiNf, setFiltroPossuiNf] = useState('')
  const [filtroLocalizacao, setFiltroLocalizacao] = useState('')

  useEffect(() => {
    listEmployees({ status: 'ativo' }).then(setEmployees)
    getObrasAtivas().then(setObras)
  }, [])

  // Auto-carregar relatório quando filtros mudarem (debounce)
  useEffect(() => {
    const t = setTimeout(() => {
      loadReport()
    }, 200)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportType, selectedEmployeeId, selectedMotivo, financeiroCategoria, financeiroTipo, dataInicial, dataFinal, tipoProduto, valorMinimo, valorMaximo, statusFuncionario, filtroObra, filtroFornecedor, filtroPossuiNf, filtroLocalizacao])

  async function openEmployeeDetails(employeeId) {
    const employee = employees.find(e => e.id === employeeId)
    if (!employee) return

    // Calcular período baseado em data personalizada ou padrão de 30 dias
    let since
    if (dataInicial && dataFinal) {
      since = new Date(dataInicial).getTime()
    } else {
      since = Date.now() - 30 * 24 * 60 * 60 * 1000
    }

    const all = await fetchMovimentacoes()
    const movements = (all || [])
      .filter(m => m.funcionarioId === employeeId && (m.dataMovimentacao ?? m.criadoEm ?? 0) >= since)
      .sort((a, b) => (b.dataMovimentacao ?? b.criadoEm ?? 0) - (a.dataMovimentacao ?? a.criadoEm ?? 0))

    setDetailModal({ open: true, employee, movements })
  }

  async function loadReport() {
    setLoading(true)
    try {
      // Calcular período baseado em data personalizada ou padrão de 30 dias
      let since, until
      if (dataInicial && dataFinal) {
        since = new Date(dataInicial).getTime()
        until = new Date(dataFinal).getTime()
      } else {
        const d = 30 // padrão de 30 dias
        since = Date.now() - d * 24 * 60 * 60 * 1000
        until = Date.now()
      }

      let result = []

      switch (reportType) {
        case 'consumo_funcionario':
          result = await getConsumoPorFuncionario({ dias: dataInicial && dataFinal ? Math.ceil((until - since) / (24 * 60 * 60 * 1000)) : 30 })
          if (selectedEmployeeId) {
            result = result.filter(r => r.funcionarioId === selectedEmployeeId)
          }
          if (statusFuncionario) {
            const filteredEmployees = employees.filter(e => e.status === statusFuncionario)
            result = result.filter(r => filteredEmployees.some(e => e.id === r.funcionarioId))
          }
          break
        case 'consumo_produto':
          result = await getConsumoPorProduto({ dias: dataInicial && dataFinal ? Math.ceil((until - since) / (24 * 60 * 60 * 1000)) : 30 })
          if (tipoProduto) {
            result = result.filter(r => r.tipo === tipoProduto)
          }
          break
        case 'perdas':
          result = await getTopPerdas({ dias: dataInicial && dataFinal ? Math.ceil((until - since) / (24 * 60 * 60 * 1000)) : 30, limit: 50 })
          if (tipoProduto) {
            result = result.filter(r => r.tipo === tipoProduto)
          }
          break
        case 'pendentes':
          result = (await fetchMovimentacoes()).filter(m => m.status === 'pendente-devolucao')
          if (dataInicial && dataFinal) {
            result = result.filter(m => (m.dataMovimentacao ?? m.criadoEm ?? 0) >= since && (m.dataMovimentacao ?? m.criadoEm ?? 0) <= until)
          }
          if (selectedEmployeeId) {
            result = result.filter(r => r.funcionarioId === selectedEmployeeId)
          }
          if (statusFuncionario) {
            const filteredEmployees = employees.filter(e => e.status === statusFuncionario)
            result = result.filter(r => filteredEmployees.some(e => e.id === r.funcionarioId))
          }
          if (tipoProduto) {
            result = result.filter(r => r.tipoProduto === tipoProduto)
          }
          if (filtroObra) {
            result = result.filter(r => r.obraId === filtroObra)
          }
          result = result.sort((a, b) => (a.dataMovimentacao ?? a.criadoEm ?? 0) - (b.dataMovimentacao ?? b.criadoEm ?? 0))
          break
        case 'epi_motivos':
          const sinceEpi = dataInicial && dataFinal ? since : Date.now() - 30 * 24 * 60 * 60 * 1000
          const untilEpi = dataInicial && dataFinal ? until : Date.now()
          result = (await fetchMovimentacoes())
            .filter(m => m.tipoProduto === 'epi' && !m.epiReutilizavel && (m.dataMovimentacao ?? m.criadoEm ?? 0) >= sinceEpi && (m.dataMovimentacao ?? m.criadoEm ?? 0) <= untilEpi && m.motivoEpi)
          if (selectedMotivo) {
            result = result.filter(r => r.motivoEpi === selectedMotivo)
          }
          if (selectedEmployeeId) {
            result = result.filter(r => r.funcionarioId === selectedEmployeeId)
          }
          if (statusFuncionario) {
            const filteredEmployees = employees.filter(e => e.status === statusFuncionario)
            result = result.filter(r => filteredEmployees.some(e => e.id === r.funcionarioId))
          }
          break
        case 'epi_vencidos':
          result = await getEPIsVencidos()
          if (tipoProduto) {
            result = result.filter(r => r.tipo === tipoProduto)
          }
          break
        case 'epi_prestes_vencer':
          result = await getEPIsPrestesVencer()
          if (tipoProduto) {
            result = result.filter(r => r.tipo === tipoProduto)
          }
          break
        case 'consumo_obra': {
          const sinceObra = dataInicial && dataFinal ? since : Date.now() - 30 * 24 * 60 * 60 * 1000
          const untilObra = dataInicial && dataFinal ? until : Date.now()
          const allMovsObra = await fetchMovimentacoes()
          const movsObra = (allMovsObra || [])
            .filter(m => m.obraId)
            .filter(m => (m.dataMovimentacao ?? m.criadoEm ?? 0) >= sinceObra && (m.dataMovimentacao ?? m.criadoEm ?? 0) <= untilObra)
            .filter(m => !filtroObra || m.obraId === filtroObra)
            .filter(m => !tipoProduto || m.tipoProduto === tipoProduto)
          const mapaObras = {}
          movsObra.forEach(m => {
            const key = m.obraId
            if (!mapaObras[key]) mapaObras[key] = { obraId: key, obraNome: m.obraNome || '—', itens: [], totalQtd: 0 }
            mapaObras[key].itens.push(m)
            mapaObras[key].totalQtd += Number(m.quantidade ?? 0)
          })
          result = Object.values(mapaObras).sort((a, b) => b.totalQtd - a.totalQtd)
          break
        }
        case 'custo_obra': {
          const sinceObra2 = dataInicial && dataFinal ? since : Date.now() - 30 * 24 * 60 * 60 * 1000
          const untilObra2 = dataInicial && dataFinal ? until : Date.now()
          const allMovsObra2 = await fetchMovimentacoes()
          const movsObra2 = (allMovsObra2 || [])
            .filter(m => m.obraId)
            .filter(m => (m.dataMovimentacao ?? m.criadoEm ?? 0) >= sinceObra2 && (m.dataMovimentacao ?? m.criadoEm ?? 0) <= untilObra2)
            .filter(m => !filtroObra || m.obraId === filtroObra)
            .filter(m => m.custoTotal != null || m.custoUnitario != null)
          const mapaObrasCusto = {}
          movsObra2.forEach(m => {
            const key = m.obraId
            const custo = m.custoTotal != null ? Number(m.custoTotal) : (m.custoUnitario != null && m.quantidade != null ? Number(m.custoUnitario) * Number(m.quantidade) : 0)
            if (!mapaObrasCusto[key]) mapaObrasCusto[key] = { obraId: key, obraNome: m.obraNome || '—', custoTotal: 0, totalMovs: 0 }
            mapaObrasCusto[key].custoTotal += custo
            mapaObrasCusto[key].totalMovs += 1
          })
          result = Object.values(mapaObrasCusto).sort((a, b) => b.custoTotal - a.custoTotal)
          break
        }
        case 'inventario_localizacao': {
          const allProdsLoc = await fetchProdutos()
          let prodsLoc = allProdsLoc.filter(p => (p.status || 'ativo') === 'ativo')
          if (filtroLocalizacao.trim()) {
            const q = filtroLocalizacao.toLowerCase()
            prodsLoc = prodsLoc.filter(p =>
              (p.localizacao ?? '').toLowerCase().includes(q) ||
              (p.locCorredor ?? '').toLowerCase().includes(q) ||
              (p.locPrateleira ?? '').toLowerCase().includes(q) ||
              (p.locGaveta ?? '').toLowerCase().includes(q)
            )
          }
          if (tipoProduto) prodsLoc = prodsLoc.filter(p => p.tipo === tipoProduto)
          // Agrupar por localização
          const mapaLoc = {}
          prodsLoc.forEach(p => {
            const loc = p.localizacao || [p.locCorredor, p.locPrateleira, p.locGaveta].filter(Boolean).join(' / ') || 'Sem localização'
            if (!mapaLoc[loc]) mapaLoc[loc] = { localizacao: loc, produtos: [], totalQtd: 0 }
            mapaLoc[loc].produtos.push(p)
            mapaLoc[loc].totalQtd += Number(p.quantidade ?? 0)
          })
          result = Object.values(mapaLoc).sort((a, b) => a.localizacao.localeCompare(b.localizacao))
          break
        }
        case 'produtos_nf': {
          const allProdsNf = await fetchProdutos()
          const allNfs = await listNotasFiscais({ status: 'ativa' })
          const nfMap = {}
          allNfs.forEach(nf => { nfMap[nf.id] = nf })
          let prodsNf = filtroPossuiNf === 'nao'
            ? allProdsNf.filter(p => !p.notaFiscalId)
            : allProdsNf.filter(p => p.notaFiscalId)
          if (filtroFornecedor.trim()) {
            const q = filtroFornecedor.toLowerCase()
            prodsNf = prodsNf.filter(p => {
              const nf = nfMap[p.notaFiscalId]
              return nf && ((nf.fornecedorNome || '').toLowerCase().includes(q) || (nf.numero || '').includes(q))
            })
          }
          if (tipoProduto) prodsNf = prodsNf.filter(p => p.tipo === tipoProduto)
          result = prodsNf.map(p => ({ ...p, nf: nfMap[p.notaFiscalId] || null }))
          break
        }
        case 'compras_fornecedor': {
          let allNfs2 = await listNotasFiscais({ status: 'ativa' })
          if (dataInicial && dataFinal) {
            allNfs2 = allNfs2.filter(n => n.criadoEm >= since && n.criadoEm <= until + 86399999)
          }
          if (filtroFornecedor.trim()) {
            const q = filtroFornecedor.toLowerCase()
            allNfs2 = allNfs2.filter(n => (n.fornecedorNome || '').toLowerCase().includes(q) || (n.cnpjFornecedor || '').includes(q))
          }
          const mapaForn = {}
          allNfs2.forEach(nf => {
            const key = nf.cnpjFornecedor || nf.fornecedorNome || 'Sem identificação'
            if (!mapaForn[key]) mapaForn[key] = { fornecedor: nf.fornecedorNome || '—', cnpj: nf.cnpjFornecedor || '—', totalValor: 0, qtdNfs: 0, nfs: [] }
            mapaForn[key].totalValor += Number(nf.valorTotal ?? 0)
            mapaForn[key].qtdNfs += 1
            mapaForn[key].nfs.push(nf)
          })
          result = Object.values(mapaForn).sort((a, b) => b.totalValor - a.totalValor)
          break
        }
        case 'financeiro': {
          const sinceFin = dataInicial && dataFinal ? since : Date.now() - 30 * 24 * 60 * 60 * 1000
          const untilFin = dataInicial && dataFinal ? until : Date.now()
          const all = await fetchMovimentacoes()
          result = (all || [])
            .filter(m => (m.criadoEm ?? m.dataMovimentacao ?? 0) >= sinceFin && (m.criadoEm ?? m.dataMovimentacao ?? 0) <= untilFin)
            .filter(m => m.custoTotal != null || m.custoUnitario != null)
            // aplicar filtros adicionais quando definidos
            .filter(m => !financeiroCategoria || (m.tipoProduto === financeiroCategoria || m.categoria === financeiroCategoria))
            .filter(m => !financeiroTipo || (m.tipo === financeiroTipo))
            .filter(m => !tipoProduto || (m.tipoProduto === tipoProduto))
            .filter(m => !filtroObra || m.obraId === filtroObra)
          if (selectedEmployeeId) {
            result = result.filter(r => r.funcionarioId === selectedEmployeeId)
          }
          if (statusFuncionario) {
            const filteredEmployees = employees.filter(e => e.status === statusFuncionario)
            result = result.filter(r => filteredEmployees.some(e => e.id === r.funcionarioId))
          }
          // Filtro de faixa de valor
          if (valorMinimo) {
            const min = Number(valorMinimo)
            result = result.filter(m => {
              const custo = m.custoTotal != null ? Number(m.custoTotal) : (m.custoUnitario != null && m.quantidade != null ? Number(m.custoUnitario) * Number(m.quantidade) : 0)
              return custo >= min
            })
          }
          if (valorMaximo) {
            const max = Number(valorMaximo)
            result = result.filter(m => {
              const custo = m.custoTotal != null ? Number(m.custoTotal) : (m.custoUnitario != null && m.quantidade != null ? Number(m.custoUnitario) * Number(m.quantidade) : 0)
              return custo <= max
            })
          }
          // Ordenar por data desc
          result = result.sort((a, b) => (b.criadoEm ?? b.dataMovimentacao ?? 0) - (a.criadoEm ?? a.dataMovimentacao ?? 0))
          break
        }
        default:
          result = []
      }

      setData(result)
    } catch (err) {
      setToastType('error')
      setToast(err?.message || 'Falha ao carregar.')
    } finally {
      setLoading(false)
    }
  }

  function exportCSV() {
    if (data.length === 0) return
    let csv = ''

    if (reportType === 'consumo_funcionario') {
      csv = 'Funcionário,Total Itens\n'
      data.forEach(r => { csv += `"${r.funcionarioNome}",${r.total}\n` })
    } else if (reportType === 'consumo_produto') {
      csv = 'Produto,Tipo,Total\n'
      data.forEach(r => { csv += `"${r.produtoNome}","${r.tipo}",${r.total}\n` })
    } else if (reportType === 'perdas') {
      csv = 'Data,Produto,Funcionário,Quantidade,Observação\n'
      data.forEach(r => { csv += `${formatDate(r.dataDevolucaoReal)},"${r.produtoNome}","${r.funcionarioNome}",${r.quantidade},"${r.observacaoDevolucao || ''}"\n` })
    } else if (reportType === 'pendentes') {
      csv = 'Data,Produto,Funcionário,Quantidade\n'
      data.forEach(r => { csv += `${formatDate(r.dataMovimentacao)},"${r.produtoNome}","${r.funcionarioNome}",${r.quantidade}\n` })
    } else if (reportType === 'epi_motivos') {
      csv = 'Data,Produto,Funcionário,Motivo,Quantidade\n'
      data.forEach(r => { csv += `${formatDate(r.dataMovimentacao)},"${r.produtoNome}","${r.funcionarioNome}",${r.motivoEpi},${r.quantidade}\n` })
    } else if (reportType === 'financeiro') {
      csv = 'Data,Produto,Funcionário,Classificação,Quantidade,Custo Unitário,Custo Total,Obra\n'
      data.forEach(r => {
        const cu = r.custoUnitario != null ? Number(r.custoUnitario).toFixed(2) : ''
        const ct = r.custoTotal != null ? Number(r.custoTotal).toFixed(2) : (r.custoUnitario != null && r.quantidade != null ? (Number(r.custoUnitario) * Number(r.quantidade)).toFixed(2) : '')
        csv += `${formatDate(r.criadoEm ?? r.dataMovimentacao)},"${r.produtoNome || ''}","${r.funcionarioNome || ''}",${r.classificacaoFinanceira || ''},${r.quantidade ?? ''},${cu},${ct},"${r.obraNome || ''}"\n`
      })
    } else if (reportType === 'consumo_obra') {
      csv = 'Obra,Total Itens,Movimentações,Produto,Funcionário,Qtd,Data\n'
      data.forEach(r => {
        if (r.itens.length === 0) {
          csv += `"${r.obraNome}",${r.totalQtd},0,,,, \n`
        } else {
          r.itens.forEach((m, i) => {
            csv += `"${i === 0 ? r.obraNome : ''}",${i === 0 ? r.totalQtd : ''},${i === 0 ? r.itens.length : ''},"${m.produtoNome || ''}","${m.funcionarioNome || ''}",${m.quantidade ?? ''},${formatDate(m.dataMovimentacao ?? m.criadoEm)}\n`
          })
        }
      })
    } else if (reportType === 'custo_obra') {
      csv = 'Obra,Custo Total,Movimentações\n'
      data.forEach(r => {
        csv += `"${r.obraNome}",${Number(r.custoTotal).toFixed(2)},${r.totalMovs}\n`
      })
    } else if (reportType === 'inventario_localizacao') {
      csv = 'Localização,Produto,Tipo,Qtd Estoque\n'
      data.forEach(r => {
        r.produtos.forEach((p, i) => {
          csv += `"${i === 0 ? r.localizacao : ''}","${p.nome || ''}",${p.tipo || ''},${p.quantidade ?? 0}\n`
        })
      })
    } else if (reportType === 'produtos_nf') {
      csv = 'Produto,Tipo,Qtd,NF Número,Fornecedor,Valor NF\n'
      data.forEach(r => {
        csv += `"${r.nome || ''}",${r.tipo || ''},${r.quantidade ?? 0},"${r.nf?.numero || ''}","${r.nf?.fornecedorNome || ''}",${r.nf?.valorTotal != null ? Number(r.nf.valorTotal).toFixed(2) : ''}\n`
      })
    } else if (reportType === 'compras_fornecedor') {
      csv = 'Fornecedor,CNPJ,Qtd NFs,Total (R$),NF Número,Série,Data Emissão,Valor NF\n'
      data.forEach(r => {
        if (r.nfs.length === 0) {
          csv += `"${r.fornecedor}",${r.cnpj},0,${Number(r.totalValor).toFixed(2)},,,, \n`
        } else {
          r.nfs.forEach((nf, i) => {
            csv += `"${i === 0 ? r.fornecedor : ''}",${i === 0 ? r.cnpj : ''},${i === 0 ? r.qtdNfs : ''},${i === 0 ? Number(r.totalValor).toFixed(2) : ''},${nf.numero || ''},${nf.serie || ''},${nf.dataEmissao || ''},${nf.valorTotal != null ? Number(nf.valorTotal).toFixed(2) : ''}\n`
          })
        }
      })
    }

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `relatorio-${reportType}-${new Date().toISOString().split('T')[0]}.csv`
    link.click()
    URL.revokeObjectURL(url)

    setToastType('success')
    setToast('CSV exportado.')
  }

  async function exportPDF() {
    if (data.length === 0) {
      setToastType('error')
      setToast('Nenhum dado para gerar PDF. Gere o relatório primeiro.')
      return
    }
    
    setToastType('info')
    setToast('Gerando PDF...')
    
    try {
      console.log('[PDF] Iniciando geração de PDF...')
      console.log('[PDF] jsPDF importado com sucesso')
      const orientation = 'landscape'
      const doc = new jsPDF(orientation)
      console.log('[PDF] Documento jsPDF criado')

      // Construir string de filtros aplicados
      const filtrosAplicados = []
      if (dataInicial && dataFinal) {
        filtrosAplicados.push(`Período: ${new Date(dataInicial).toLocaleDateString('pt-BR')} a ${new Date(dataFinal).toLocaleDateString('pt-BR')}`)
      } else {
        filtrosAplicados.push(`Período: Últimos 30 dias`)
      }
      if (selectedEmployeeId) {
        const emp = employees.find(e => e.id === selectedEmployeeId)
        filtrosAplicados.push(`Funcionário: ${emp?.nome || ''}`)
      }
      if (tipoProduto) {
        filtrosAplicados.push(`Tipo Produto: ${tipoProduto}`)
      }
      if (statusFuncionario) {
        filtrosAplicados.push(`Status: ${statusFuncionario}`)
      }
      if (selectedMotivo) {
        filtrosAplicados.push(`Motivo: ${selectedMotivo}`)
      }
      if (financeiroCategoria) {
        filtrosAplicados.push(`Categoria: ${financeiroCategoria}`)
      }
      if (financeiroTipo) {
        filtrosAplicados.push(`Tipo Mov: ${financeiroTipo}`)
      }
      if (valorMinimo) {
        filtrosAplicados.push(`Valor ≥ R$ ${Number(valorMinimo).toFixed(2)}`)
      }
      if (valorMaximo) {
        filtrosAplicados.push(`Valor ≤ R$ ${Number(valorMaximo).toFixed(2)}`)
      }

      const filtrosTexto = filtrosAplicados.length > 0 ? filtrosAplicados.join(' • ') : 'Nenhum filtro'

      await drawPDFHeader(doc, {
        titulo: 'RELATÓRIO',
        subtitulo: `${getReportDescription(reportType)}`,
        usuario: session?.usuarioNome || 'Sistema',
        infoExtra: `Registros: ${data.length} | ${filtrosTexto}`,
        landscape: orientation === 'landscape'
      })

      let y = orientation === 'landscape' ? 75 : 60

      // Desenhar tabela baseada no tipo de relatório (modo paisagem)
      if (reportType === 'consumo_funcionario') {
        // Tabela única com todos os itens de todos os funcionários filtrados
        doc.setTextColor(0, 0, 0)
        const colWidths = [65, 120, 45, 35] // Total: 265mm (dentro de 269mm útil)
        const headers = ['Funcionário', 'Produto', 'Data', 'Qtd']
        drawTableHeader(doc, headers, y, colWidths, 'left')
        y += 9
        data.forEach((item) => {
          const lista = Array.isArray(item.itens) ? [...item.itens] : []
          lista.sort((a, b) => (b.data ?? 0) - (a.data ?? 0))
          if (lista.length === 0) {
            // funcionário sem itens: mostrar só o nome
            y = checkNewPage(doc, y, 185, headers, colWidths, 'left')
            drawTableRow(doc, [item.funcionarioNome || '—', '—', '—', '0'], y, colWidths, 'left')
            y += 7
          } else {
            lista.forEach((it, idx) => {
              y = checkNewPage(doc, y, 185, headers, colWidths, 'left')
              drawTableRow(doc, [
                idx === 0 ? (item.funcionarioNome || '—') : '',
                it.produto || '—',
                formatDate(it.data),
                String(it.quantidade ?? '')
              ], y, colWidths, 'left')
              y += 7
            })
          }
        })
      } else if (reportType === 'consumo_produto') {
        const colWidths = [20, 120, 45, 80] // Total: 265mm
        const headers = ['#', 'Produto', 'Tipo', 'Total']
        drawTableHeader(doc, headers, y, colWidths, 'center')
        y += 10
        data.forEach((item, idx) => {
          y = checkNewPage(doc, y, 180, headers, colWidths, 'center')
          drawTableRow(doc, [String(idx + 1), item.produtoNome, item.tipo, String(item.total)], y, colWidths, 'center')
          y += 7
        })
      } else if (reportType === 'perdas') {
        const colWidths = [20, 45, 110, 70, 20] // Total: 265mm
        const headers = ['#', 'Data', 'Produto', 'Funcionário', 'Qtd']
        drawTableHeader(doc, headers, y, colWidths, 'center')
        y += 10
        data.forEach((item, idx) => {
          y = checkNewPage(doc, y, 180, headers, colWidths, 'center')
          drawTableRow(doc, [String(idx + 1), formatDate(item.dataDevolucaoReal), item.produtoNome, item.funcionarioNome, String(item.quantidade)], y, colWidths, 'center')
          y += 8
          if (item.observacaoDevolucao && y < 180) {
            doc.setFontSize(8)
            doc.setTextColor(100, 100, 100)
            doc.text(`Obs: ${item.observacaoDevolucao}`, 20, y)
            y += 5
            doc.setFontSize(9)
            doc.setTextColor(0, 0, 0)
          }
        })
      } else if (reportType === 'pendentes') {
        const colWidths = [15, 100, 70, 25, 55] // Total: 265mm
        const headers = ['#', 'Produto', 'Funcionário', 'Qtd', 'Data']
        drawTableHeader(doc, headers, y, colWidths, 'center')
        y += 10
        data.forEach((item, idx) => {
          y = checkNewPage(doc, y, 180, headers, colWidths, 'center')
          drawTableRow(doc, [String(idx + 1), item.produtoNome, item.funcionarioNome, String(item.quantidade), formatDate(item.dataMovimentacao)], y, colWidths, 'center')
          y += 7
        })
      } else if (reportType === 'epi_motivos') {
        const colWidths = [15, 45, 85, 55, 35, 30] // Total: 265mm
        const headers = ['#', 'Data', 'Produto', 'Funcionário', 'Motivo', 'Qtd']
        drawTableHeader(doc, headers, y, colWidths, 'center')
        y += 10
        data.forEach((item, idx) => {
          y = checkNewPage(doc, y, 180, headers, colWidths, 'center')
          drawTableRow(doc, [String(idx + 1), formatDate(item.dataMovimentacao), item.produtoNome, item.funcionarioNome, item.motivoEpi || '-', String(item.quantidade)], y, colWidths, 'center')
          y += 7
        })
      } else if (reportType === 'epi_vencidos') {
        const colWidths = [15, 110, 80, 35, 25] // Total: 265mm
        const headers = ['#', 'Produto', 'Funcionário', 'Vencimento', 'Qtd']
        drawTableHeader(doc, headers, y, colWidths, 'center')
        y += 10
        data.forEach((item, idx) => {
          y = checkNewPage(doc, y, 180, headers, colWidths, 'center')
          const diasVencido = Math.floor((new Date() - new Date(item.dataValidade)) / (1000 * 60 * 60 * 24))
          const rowData = [String(idx + 1), item.nome, String(item.quantidade || 0), new Date(item.dataValidade).toLocaleDateString('pt-BR'), String(diasVencido)]
          let x = 14
          rowData.forEach((cellData, cellIdx) => {
            drawTableCell(doc, x, y, colWidths[cellIdx], 8, cellData, 'center', false)
            x += colWidths[cellIdx]
          })
          y += 7
        })
      } else if (reportType === 'epi_prestes_vencer') {
        const colWidths = [15, 115, 75, 35, 25] // Total: 265mm
        const headers = ['#', 'Produto', 'Quantidade', 'Data Validade', 'Dias']
        drawTableHeader(doc, headers, y, colWidths, 'center')
        y += 10
        data.forEach((item, idx) => {
          y = checkNewPage(doc, y, 180, headers, colWidths, 'center')
          const diasParaVencer = Math.ceil((new Date(item.dataValidade) - new Date()) / (1000 * 60 * 60 * 24))
          const rowData = [String(idx + 1), item.nome, String(item.quantidade || 0), new Date(item.dataValidade).toLocaleDateString('pt-BR'), String(diasParaVencer)]
          let x = 14
          rowData.forEach((cellData, cellIdx) => {
            drawTableCell(doc, x, y, colWidths[cellIdx], 8, cellData, 'center', false)
            x += colWidths[cellIdx]
          })
          y += 7
        })
      } else if (reportType === 'financeiro') {
        // Landscape: largura útil 269mm (297 - margem 14 × 2)
        // Colunas: 25+80+55+30+15+30+30 = 265mm
        doc.setTextColor(0, 0, 0)
        const headers = ['Data', 'Produto', 'Funcionário', 'Classificação', 'Qtd', 'Unitário', 'Total']
        const colWidths = [25, 80, 55, 30, 15, 30, 30]
        drawTableHeader(doc, headers, y, colWidths, 'left')
        y += 9
        data.forEach(r => {
          const ct = r.custoTotal != null ? Number(r.custoTotal) : (r.custoUnitario != null && r.quantidade != null ? Number(r.custoUnitario) * Number(r.quantidade) : 0)
          const cu = r.custoUnitario != null ? Number(r.custoUnitario) : null
          const row = [
            formatDate(r.criadoEm ?? r.dataMovimentacao),
            r.produtoNome || '—',
            r.funcionarioNome || '—',
            r.classificacaoFinanceira || '—',
            String(r.quantidade ?? ''),
            cu != null ? `R$ ${cu.toFixed(2)}` : '—',
            `R$ ${ct.toFixed(2)}`
          ]
          y = checkNewPage(doc, y, 185, headers, colWidths, 'left')
          drawTableRow(doc, row, y, colWidths, 'left')
          y += 7
        })
        const fileName = `relatorio-${reportType}-${new Date().toISOString().split('T')[0]}.pdf`
        doc.save(fileName)
        setToastType('success')
        setToast(`PDF gerado: ${fileName}`)
        return
      } else if (reportType === 'consumo_obra') {
        const headers = ['Obra', 'Produto', 'Funcionário', 'Qtd', 'Data']
        const colWidths = [60, 85, 70, 20, 30]
        drawTableHeader(doc, headers, y, colWidths, 'left')
        y += 9
        data.forEach(r => {
          if (r.itens.length === 0) {
            y = checkNewPage(doc, y, 185, headers, colWidths, 'left')
            drawTableRow(doc, [r.obraNome, '—', '—', '0', '—'], y, colWidths, 'left')
            y += 7
          } else {
            r.itens.forEach((m, i) => {
              y = checkNewPage(doc, y, 185, headers, colWidths, 'left')
              drawTableRow(doc, [i === 0 ? r.obraNome : '', m.produtoNome || '—', m.funcionarioNome || '—', String(m.quantidade ?? ''), formatDate(m.dataMovimentacao ?? m.criadoEm)], y, colWidths, 'left')
              y += 7
            })
          }
        })
      } else if (reportType === 'custo_obra') {
        const headers = ['Obra', 'Custo Total', 'Movimentações']
        const colWidths = [150, 70, 45]
        drawTableHeader(doc, headers, y, colWidths, 'left')
        y += 9
        data.forEach(r => {
          y = checkNewPage(doc, y, 185, headers, colWidths, 'left')
          drawTableRow(doc, [r.obraNome, `R$ ${Number(r.custoTotal).toFixed(2)}`, String(r.totalMovs)], y, colWidths, 'left')
          y += 7
        })
      } else if (reportType === 'inventario_localizacao') {
        const headers = ['Localização', 'Produto', 'Tipo', 'Qtd']
        const colWidths = [70, 120, 40, 35]
        drawTableHeader(doc, headers, y, colWidths, 'left')
        y += 9
        data.forEach(r => {
          r.produtos.forEach((p, i) => {
            y = checkNewPage(doc, y, 185, headers, colWidths, 'left')
            drawTableRow(doc, [i === 0 ? r.localizacao : '', p.nome || '—', p.tipo || '—', String(p.quantidade ?? 0)], y, colWidths, 'left')
            y += 7
          })
        })
      } else if (reportType === 'produtos_nf') {
        const headers = ['Produto', 'Tipo', 'Qtd', 'NF Número', 'Fornecedor']
        const colWidths = [80, 30, 20, 45, 90]
        drawTableHeader(doc, headers, y, colWidths, 'left')
        y += 9
        data.forEach(r => {
          y = checkNewPage(doc, y, 185, headers, colWidths, 'left')
          drawTableRow(doc, [r.nome || '—', r.tipo || '—', String(r.quantidade ?? 0), r.nf?.numero || '—', r.nf?.fornecedorNome || '—'], y, colWidths, 'left')
          y += 7
        })
      } else if (reportType === 'compras_fornecedor') {
        const headers = ['Fornecedor', 'CNPJ', 'NFs', 'Total']
        const colWidths = [90, 60, 20, 50]
        drawTableHeader(doc, headers, y, colWidths, 'left')
        y += 9
        data.forEach(r => {
          y = checkNewPage(doc, y, 185, headers, colWidths, 'left')
          drawTableRow(doc, [r.fornecedor, r.cnpj, String(r.qtdNfs), `R$ ${Number(r.totalValor).toFixed(2)}`], y, colWidths, 'left')
          y += 7
          r.nfs.forEach(nf => {
            y = checkNewPage(doc, y, 185, headers, colWidths, 'left')
            doc.setFontSize(8)
            doc.setTextColor(150, 150, 150)
            doc.text(`  NF ${nf.numero || '—'}${nf.serie ? `/${nf.serie}` : ''} • ${nf.dataEmissao || '—'} • ${nf.valorTotal != null ? `R$ ${Number(nf.valorTotal).toFixed(2)}` : '—'}`, 16, y)
            doc.setFontSize(9)
            doc.setTextColor(0, 0, 0)
            y += 6
          })
        })
      }

      const fileName = `relatorio-${reportType}-${new Date().toISOString().split('T')[0]}.pdf`
      console.log('[PDF] Salvando PDF:', fileName)
      doc.save(fileName)
      console.log('[PDF] PDF salvo com sucesso')

      setToastType('success')
      setToast(`PDF gerado: ${fileName}`)
    } catch (err) {
      console.error('[PDF] Erro ao gerar PDF:', err)
      console.error('[PDF] Detalhes do erro:', JSON.stringify(err))
      setToastType('error')
      
      // Solução final absoluta - mensagem sempre visível
      let errorMessage = 'Erro ao gerar PDF. Tente novamente.'
      
      try {
        if (err && typeof err === 'object' && err.message) {
          errorMessage = err.message
        } else if (typeof err === 'string') {
          errorMessage = err
        }
      } catch (e) {
        errorMessage = 'Erro ao gerar PDF. Tente novamente.'
      }
      
      setToast(errorMessage)
    }
  }

  const title = useMemo(() => REPORT_TYPES.find(r => r.id === reportType)?.label || 'Relatório', [reportType])

  return (
    <div className="space-y-4">
      <Toast message={toast} type={toastType} onClose={() => setToast(null)} />

      <BackButton />

      <div className="rounded-2xl cm-card p-4">
        <div className="text-sm font-semibold">Relatórios</div>
        <div className="mt-1 text-xs text-white/70">Filtre e exporte dados do sistema.</div>
      </div>

      <div className="rounded-2xl cm-card p-4">
        <div className="text-xs text-white/70 mb-2">Filtros do relatório</div>
        
        <div className="space-y-3">
          <div>
            <div className="text-xs text-white/60">Tipo de relatório</div>
            <select
              value={reportType}
              onChange={(e) => setReportType(e.target.value)}
              className="mt-1 w-full rounded-xl bg-white/10 px-3 py-3 text-sm"
            >
              {REPORT_TYPES.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="text-xs text-white/60">Data Inicial</div>
              <input
                type="date"
                value={dataInicial}
                onChange={(e) => setDataInicial(e.target.value)}
                className="mt-1 w-full rounded-xl bg-white/10 px-3 py-3 text-sm outline-none"
              />
            </div>
            <div>
              <div className="text-xs text-white/60">Data Final</div>
              <input
                type="date"
                value={dataFinal}
                onChange={(e) => setDataFinal(e.target.value)}
                className="mt-1 w-full rounded-xl bg-white/10 px-3 py-3 text-sm outline-none"
              />
            </div>
            {(reportType === 'consumo_funcionario' || reportType === 'pendentes' || reportType === 'epi_vencidos' || reportType === 'epi_prestes_vencer' || reportType === 'financeiro') && (
              <div>
                <div className="text-xs text-white/60">Funcionário</div>
                <select
                  value={selectedEmployeeId}
                  onChange={(e) => setSelectedEmployeeId(e.target.value)}
                  className="mt-1 w-full rounded-xl bg-white/10 px-3 py-3 text-sm"
                >
                  <option value="">Todos</option>
                  {employees.map(e => <option key={e.id} value={e.id}>{e.nome}</option>)}
                </select>
              </div>
            )}
            
            {reportType === 'epi_motivos' && (
              <div>
                <div className="text-xs text-white/60">Motivo</div>
                <select
                  value={selectedMotivo}
                  onChange={(e) => setSelectedMotivo(e.target.value)}
                  className="mt-1 w-full rounded-xl bg-white/10 px-3 py-3 text-sm"
                >
                  {EPI_MOTIVOS.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
                </select>
              </div>
            )}
            {reportType === 'financeiro' && (
              <>
                <div>
                  <div className="text-xs text-white/60">Categoria</div>
                  <select
                    value={financeiroCategoria}
                    onChange={(e) => setFinanceiroCategoria(e.target.value)}
                    className="mt-1 w-full rounded-xl bg-white/10 px-3 py-3 text-sm"
                  >
                    <option value="">Todas</option>
                    <option value="material">Material</option>
                    <option value="ferramenta">Ferramenta</option>
                    <option value="epi">EPI</option>
                  </select>
                </div>
                <div>
                  <div className="text-xs text-white/60">Tipo de movimentação</div>
                  <select
                    value={financeiroTipo}
                    onChange={(e) => setFinanceiroTipo(e.target.value)}
                    className="mt-1 w-full rounded-xl bg-white/10 px-3 py-3 text-sm"
                  >
                    <option value="">Todos</option>
                    <option value="entrega">Entrega</option>
                    <option value="saida">Saída</option>
                    <option value="perda">Perda</option>
                    <option value="descarte">Descarte</option>
                    <option value="manutencao">Manutenção</option>
                  </select>
                </div>
              </>
            )}
          </div>

          {/* Filtro de Obra */}
          {['consumo_funcionario','consumo_produto','pendentes','financeiro','consumo_obra','custo_obra'].includes(reportType) && (
            <div>
              <div className="text-xs text-white/60">Obra</div>
              <select
                value={filtroObra}
                onChange={(e) => setFiltroObra(e.target.value)}
                className="mt-1 w-full rounded-xl bg-white/10 px-3 py-3 text-sm"
              >
                <option value="">Todas</option>
                {obras.map(o => <option key={o.id} value={o.id}>{o.nome}</option>)}
              </select>
            </div>
          )}

          {/* Filtro Localização */}
          {reportType === 'inventario_localizacao' && (
            <div>
              <div className="text-xs text-white/60">Localização / Corredor</div>
              <input
                value={filtroLocalizacao}
                onChange={(e) => setFiltroLocalizacao(e.target.value)}
                className="mt-1 w-full rounded-xl bg-white/10 px-3 py-3 text-sm outline-none"
                placeholder="Ex: A1, Corredor 2..."
              />
            </div>
          )}

          {/* Filtro Fornecedor / Possui NF */}
          {['produtos_nf','compras_fornecedor'].includes(reportType) && (
            <div className="space-y-2">
              <div>
                <div className="text-xs text-white/60">Fornecedor (nome ou CNPJ)</div>
                <input
                  value={filtroFornecedor}
                  onChange={(e) => setFiltroFornecedor(e.target.value)}
                  className="mt-1 w-full rounded-xl bg-white/10 px-3 py-3 text-sm outline-none"
                  placeholder="Ex: Construtora ABC"
                />
              </div>
              {reportType === 'produtos_nf' && (
                <div>
                  <div className="text-xs text-white/60">Possui NF?</div>
                  <select
                    value={filtroPossuiNf}
                    onChange={(e) => setFiltroPossuiNf(e.target.value)}
                    className="mt-1 w-full rounded-xl bg-white/10 px-3 py-3 text-sm"
                  >
                    <option value="">Com NF</option>
                    <option value="nao">Sem NF</option>
                  </select>
                </div>
              )}
            </div>
          )}

          {/* Filtros Avançados */}
          <div className="border-t border-white/10 pt-3">
            <div className="text-xs text-white/60 mb-2">Filtros Avançados</div>
            <div className="grid grid-cols-2 gap-2">
              {reportType !== 'epi_vencidos' && reportType !== 'epi_prestes_vencer' && (
                <div>
                  <div className="text-xs text-white/60">Tipo de Produto</div>
                  <select
                    value={tipoProduto}
                    onChange={(e) => setTipoProduto(e.target.value)}
                    className="mt-1 w-full rounded-xl bg-white/10 px-3 py-3 text-sm"
                  >
                    <option value="">Todos</option>
                    <option value="material">Material</option>
                    <option value="ferramenta">Ferramenta</option>
                    <option value="epi">EPI</option>
                  </select>
                </div>
              )}
              {(reportType === 'consumo_funcionario' || reportType === 'pendentes' || reportType === 'epi_motivos' || reportType === 'financeiro') && (
                <div>
                  <div className="text-xs text-white/60">Status Funcionário</div>
                  <select
                    value={statusFuncionario}
                    onChange={(e) => setStatusFuncionario(e.target.value)}
                    className="mt-1 w-full rounded-xl bg-white/10 px-3 py-3 text-sm"
                  >
                    <option value="">Todos</option>
                    <option value="ativo">Ativo</option>
                    <option value="inativo">Inativo</option>
                  </select>
                </div>
              )}
              {reportType === 'financeiro' && (
                <>
                  <div>
                    <div className="text-xs text-white/60">Valor Mínimo (R$)</div>
                    <input
                      type="number"
                      step="0.01"
                      value={valorMinimo}
                      onChange={(e) => setValorMinimo(e.target.value)}
                      className="mt-1 w-full rounded-xl bg-white/10 px-3 py-3 text-sm outline-none"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <div className="text-xs text-white/60">Valor Máximo (R$)</div>
                    <input
                      type="number"
                      step="0.01"
                      value={valorMaximo}
                      onChange={(e) => setValorMaximo(e.target.value)}
                      className="mt-1 w-full rounded-xl bg-white/10 px-3 py-3 text-sm outline-none"
                      placeholder="0.00"
                    />
                  </div>
                </>
              )}
            </div>
          </div>

          <button
            disabled={loading}
            onClick={loadReport}
            className="w-full rounded-xl bg-accent-600 px-3 py-3 text-sm font-semibold text-white disabled:opacity-60"
          >
            {loading ? 'Carregando…' : 'Gerar relatório'}
          </button>
        </div>
      </div>

      {data.length > 0 && (
        <div className="rounded-2xl cm-card p-4">
          <div className="flex items-center justify-between">
            <div className="text-xs font-semibold">{title}</div>
            <div className="flex gap-2">
              <button onClick={exportCSV} className="rounded-xl bg-white/10 px-3 py-2 text-xs font-semibold">CSV</button>
              <button onClick={exportPDF} className="rounded-xl bg-accent-600 px-3 py-2 text-xs font-semibold text-white">PDF</button>
            </div>
          </div>
          <div className="mt-3 space-y-2 max-h-80 overflow-y-auto">
            {reportType === 'consumo_funcionario' && data.map(r => (
              <div key={r.funcionarioId} className="rounded-xl bg-white/5 p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-semibold">{r.funcionarioNome}</div>
                    <div className="text-xs text-white/70">Total: {r.total} itens</div>
                  </div>
                  <button
                    onClick={() => openEmployeeDetails(r.funcionarioId)}
                    className="rounded-lg bg-accent-600 px-3 py-1 text-xs font-semibold text-white"
                  >
                    Detalhes
                  </button>
                </div>
                {Array.isArray(r.itens) && r.itens.length > 0 && (
                  <div className="mt-2 text-[11px] text-white/70">
                    {(r.itens.slice(0,5)).map((it, idx) => (
                      <div key={idx} className="flex justify-between">
                        <div className="truncate max-w-[60%]">{it.produto}</div>
                        <div className="text-white/60">Qtd: {it.quantidade ?? '—'}</div>
                        <div className="text-white/60">{formatDate(it.data)}</div>
                      </div>
                    ))}
                    {r.itens.length > 5 && (
                      <div className="text-white/50 mt-1">… e mais {r.itens.length - 5} itens</div>
                    )}
                  </div>
                )}
              </div>
            ))}
            {reportType === 'consumo_produto' && data.map(r => (
              <div key={r.produtoId} className="rounded-xl bg-white/5 p-3">
                <div className="text-sm font-semibold">{r.produtoNome}</div>
                <div className="text-xs text-white/70">{r.tipo} • Total: {r.total}</div>
              </div>
            ))}
            {reportType === 'perdas' && data.map(r => (
              <div key={r.id} className="rounded-xl bg-white/5 p-3">
                <div className="text-sm font-semibold">{r.produtoNome}</div>
                <div className="text-xs text-white/70">{formatDate(r.dataDevolucaoReal)} • {r.funcionarioNome} • Qtd: {r.quantidade}</div>
                {r.observacaoDevolucao && <div className="mt-1 text-xs text-white/50">Obs: {r.observacaoDevolucao}</div>}
              </div>
            ))}
            {reportType === 'pendentes' && data.map(r => (
              <div key={r.id} className="rounded-xl bg-white/5 p-3">
                <div className="text-sm font-semibold">{r.produtoNome}</div>
                <div className="text-xs text-white/70">{formatDate(r.dataMovimentacao)} • {r.funcionarioNome} • Qtd: {r.quantidade}</div>
              </div>
            ))}
            {reportType === 'epi_motivos' && data.map(r => (
              <div key={r.id} className="rounded-xl bg-white/5 p-3">
                <div className="text-sm font-semibold">{r.produtoNome}</div>
                <div className="text-xs text-white/70">{formatDate(r.dataMovimentacao)} • {r.funcionarioNome} • Motivo: {r.motivoEpi} • Qtd: {r.quantidade}</div>
              </div>
            ))}
            {reportType === 'consumo_obra' && data.map(r => (
              <div key={r.obraId} className="rounded-xl bg-white/5 p-3">
                <div className="text-sm font-semibold">🏗️ {r.obraNome}</div>
                <div className="text-xs text-white/70">Total: {r.totalQtd} itens • {r.itens.length} movimentações</div>
                <div className="mt-2 space-y-1">
                  {r.itens.slice(0, 5).map((m, i) => (
                    <div key={i} className="flex justify-between text-[11px] text-white/60">
                      <span className="truncate max-w-[55%]">{m.produtoNome}</span>
                      <span>{m.funcionarioNome} • Qtd: {m.quantidade}</span>
                    </div>
                  ))}
                  {r.itens.length > 5 && <div className="text-[11px] text-white/40">… e mais {r.itens.length - 5} itens</div>}
                </div>
              </div>
            ))}
            {reportType === 'custo_obra' && data.map(r => (
              <div key={r.obraId} className="rounded-xl bg-white/5 p-3">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold">🏗️ {r.obraNome}</div>
                  <div className="text-sm font-semibold text-emerald-400">
                    {Number(r.custoTotal).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                  </div>
                </div>
                <div className="text-xs text-white/60">{r.totalMovs} movimentações com custo</div>
              </div>
            ))}
            {reportType === 'inventario_localizacao' && data.map(r => (
              <div key={r.localizacao} className="rounded-xl bg-white/5 p-3">
                <div className="text-sm font-semibold">📍 {r.localizacao}</div>
                <div className="text-xs text-white/60">{r.produtos.length} produto(s) • Total estoque: {r.totalQtd}</div>
                <div className="mt-2 space-y-1">
                  {r.produtos.map((p, i) => (
                    <div key={i} className="flex justify-between text-[11px] text-white/60">
                      <span className="truncate max-w-[60%]">{p.nome}</span>
                      <span>{p.tipo} • Qtd: {p.quantidade ?? 0}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {reportType === 'produtos_nf' && data.map(r => (
              <div key={r.id} className="rounded-xl bg-white/5 p-3">
                <div className="text-sm font-semibold">{r.nome}</div>
                <div className="text-xs text-white/70">{r.tipo} • Qtd: {r.quantidade ?? 0}</div>
                {r.nf ? (
                  <div className="mt-1 text-xs text-emerald-400">
                    📎 NF {r.nf.numero}{r.nf.fornecedorNome ? ` • ${r.nf.fornecedorNome}` : ''}
                    {r.nf.valorTotal ? ` • ${Number(r.nf.valorTotal).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}` : ''}
                  </div>
                ) : (
                  <div className="mt-1 text-xs text-amber-400">⚠️ Sem nota fiscal</div>
                )}
              </div>
            ))}
            {reportType === 'compras_fornecedor' && data.map((r, idx) => (
              <div key={r.cnpj !== '—' ? r.cnpj : idx} className="rounded-xl bg-white/5 p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-semibold">{r.fornecedor}</div>
                    <div className="text-xs text-white/60">CNPJ: {r.cnpj} • {r.qtdNfs} NF{r.qtdNfs !== 1 ? 's' : ''}</div>
                  </div>
                  <div className="text-sm font-semibold text-emerald-400">
                    {Number(r.totalValor).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                  </div>
                </div>
                <div className="mt-2 space-y-1">
                  {r.nfs.map((nf, i) => (
                    <div key={i} className="flex justify-between text-[11px] text-white/50">
                      <span>NF {nf.numero}{nf.serie ? `/${nf.serie}` : ''}</span>
                      <span>{nf.dataEmissao ? new Date(nf.dataEmissao + 'T00:00:00').toLocaleDateString('pt-BR') : '—'} • {nf.valorTotal ? Number(nf.valorTotal).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '—'}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.length === 0 && !loading && (
        <div className="rounded-2xl cm-card p-4 text-sm text-white/70">Nenhum dado. Clique em "Gerar relatório".</div>
      )}

      {/* Modal de Detalhes */}
      {detailModal.open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={() => setDetailModal({ open: false, employee: null, movements: [] })}
        >
          <div
            className="w-full max-w-lg rounded-2xl bg-[#0b2948] p-4 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-sm font-semibold">{detailModal.employee?.nome}</div>
                <div className="text-xs text-white/70">Movimentações recentes</div>
              </div>
              <button
                onClick={() => setDetailModal({ open: false, employee: null, movements: [] })}
                className="rounded-lg bg-white/10 px-3 py-1 text-xs hover:bg-white/20"
              >
                Fechar
              </button>
            </div>

            <div className="space-y-3">
              {detailModal.movements.length === 0 ? (
                <div className="text-sm text-white/70">Nenhuma movimentação encontrada.</div>
              ) : (
                detailModal.movements.map((m) => (
                  <div key={m.id} className="rounded-xl bg-white/5 p-3">
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-semibold">{m.produtoNome}</div>
                      <div className={`rounded-lg px-2 py-1 text-[10px] ${
                        m.status === 'pendente-devolucao' ? 'bg-amber-500/90 text-white' :
                        m.status === 'devolvido' ? 'bg-emerald-600/90 text-white' :
                        'bg-blue-600/90 text-white'
                      }`}>
                        {m.status === 'pendente-devolucao' ? 'Pendente' :
                         m.status === 'devolvido' ? 'Devolvido' :
                         m.status === 'entregue' ? 'Entregue' : m.status}
                      </div>
                    </div>
                    <div className="mt-1 text-xs text-white/70">
                      {formatDate(m.dataMovimentacao)} • Qtd: {m.quantidade}
                    </div>
                    {m.fotoEntrega && (
                      <div className="mt-2">
                        <div className="text-xs text-white/60 mb-1">Foto da entrega</div>
                        <img
                          src={m.fotoEntrega}
                          alt="Foto da entrega"
                          className="w-full rounded-lg object-cover max-h-32"
                        />
                      </div>
                    )}
                    {m.assinaturaFuncionario && (
                      <div className="mt-2">
                        <div className="text-xs text-white/60 mb-1">Assinatura</div>
                        <img
                          src={m.assinaturaFuncionario}
                          alt="Assinatura"
                          className="w-full rounded-lg bg-white p-1 max-h-24"
                        />
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
